<template>
  <div class="login-container">
    <img src="@/assets/logo.png" alt="Logo" />
    <div>
      <div class="role-container">
        <div>
          <input
            type="radio"
            id="consumer"
            name="role"
            value="consumer"
            v-model="role"
            checked
          />
          <label for="consumer">일반회원</label>
        </div>
        <div>
          <input
            type="radio"
            v-model="role"
            id="admin"
            name="role"
            value="admin"
          />
          <label for="admin">관리자</label>
        </div>
      </div>
      <div class="form">
        <input
          type="text"
          v-model="phone"
          required
          inputmode="numeric"
          maxlength="11"
          placeholder="휴대폰 번호 [-(하이픈) 제외]"
          @input="(e) => onlyNumber(e, 'phone')"
          @keyup.enter="login"
        />
      </div>
      <div class="form">
        <input
          type="password"
          v-model="password"
          required
          maxlength="16"
          placeholder="비밀번호 입력"
          @keyup.enter="login"
        />
      </div>
      <button @click="login">로그인</button>
      <div class="tool-container">
        <button @click="openSearchPwDialog">비밀번호 초기화</button>
        <hr />
        <button @click="goJoinPage">회원가입</button>
      </div>
    </div>
    <p v-if="errorMessage" class="error">{{ errorMessage }}</p>
    <dialog ref="searchPwDialogRef">
      <header>
        <h2>비밀번호 초기화</h2>
        <button @click="closeSearchPwDialog">
          <span class="material-icons-round">close</span>
        </button>
      </header>
      <main>
        <input type="text" v-model="searchPwName" placeholder="가입자 이름" />
        <input
          type="text"
          v-model="searchPwPhone"
          placeholder="가입자 휴대폰 번호"
          inputmode="numeric"
          maxlength="11"
          @input="(e) => onlyNumber(e, 'phone')"
        />
        <button @click="searchPw">비밀번호 초기화</button>
      </main>
    </dialog>
  </div>
</template>

<script setup>
import { ref } from "vue";
import { db } from "@/lib/firebase";
import { useRouter } from "vue-router";
import {
  collection,
  getDocs,
  query,
  Timestamp,
  updateDoc,
  where,
} from "firebase/firestore";
import { sendPpurioMessage } from "@/lib/ppurio";
import { generateTempPasswordCryptoJS } from "@/lib/utils";
import { encrypt } from "@/lib/crypto";
import { loginProcess } from "@/lib/auth";
import { useBusyLock } from "@/lib/useBusyLock";

const { runLocked } = useBusyLock();

const phone = ref("");
const password = ref("");
const errorMessage = ref("");
const role = ref("consumer");

const searchPwDialogRef = ref(null);
const searchPwName = ref("");
const searchPwPhone = ref("");

const router = useRouter();

// --- 숫자만 입력 허용
const onlyNumber = (e, refName) => {
  const targetRef = {
    phone,
    searchPwPhone,
  }[refName];

  if (!targetRef) return;

  const value = String(e.target.value || "").replace(/\D/g, "");
  targetRef.value = value;
  e.target.value = value;
};

// --- 회원가입 페이지 이동
const goJoinPage = () => {
  router.push("/member");
};

// --- 비밀번호 찾기 다이얼로그 열기
const openSearchPwDialog = () => {
  errorMessage.value = "";
  searchPwName.value = "";
  searchPwPhone.value = "";
  searchPwDialogRef.value?.showModal?.();
};

// --- 비밀번호 찾기 다이얼로그 닫기
const closeSearchPwDialog = () => {
  searchPwDialogRef.value?.close?.();
};

// --- 비밀번호 찾기(임시 비밀번호 발급)
const searchPw = () =>
  runLocked(async () => {
    errorMessage.value = "";

    if (!searchPwName.value.trim()) {
      alert("이름을 입력해주세요!");
      return;
    }

    if (!searchPwPhone.value || searchPwPhone.value.length !== 11) {
      alert("휴대폰 번호를 정확히 입력해주세요!");
      return;
    }

    const snap = await getDocs(
      query(
        collection(db, "users"),
        where("userName", "==", searchPwName.value.trim()),
        where("userPhone", "==", searchPwPhone.value)
      )
    );

    if (snap.empty) {
      errorMessage.value = "회원 정보가 없습니다. 회원가입을 진행해주세요!";
      alert("일치하는 정보가 없습니다.");
      return;
    }

    // --- 첫 번째 일치 회원 대상으로 임시 비밀번호 발급
    const userDoc = snap.docs[0];
    const tempPassword = await generateTempPasswordCryptoJS(
      searchPwPhone.value
    );

    // --- encrypt가 동기/비동기 모두 대응
    const encryptedTempPassword = await Promise.resolve(encrypt(tempPassword));

    await updateDoc(userDoc.ref, {
      userPassword: encryptedTempPassword,
    });

    await sendPpurioMessage({
      targets: [{ to: searchPwPhone.value }],
      targetCount: 1,
      content: `[네코쿠모] 임시 비밀번호를 전송해드립니다!\n\n임시 비밀번호: ${tempPassword}\n\n계정 보안을 위해 로그인 후 회원정보 수정에서 비밀번호를 변경해주시기 바랍니다!\n오늘도 즐거운 쇼핑 되시길 바랍니다 :)`,
      refKey: `RESET_${Timestamp.now().seconds}_${searchPwPhone.value}`,
    });

    alert("임시 비밀번호를 문자로 전송하였습니다.\n문자 메시지를 확인하세요.");
    closeSearchPwDialog();
  }).catch((error) => {
    console.error("임시 비밀번호 발급 실패:", error);
    alert("비밀번호 찾기 처리 중 오류가 발생했습니다.");
  });

// --- 로그인 처리
const login = () =>
  runLocked(async () => {
    errorMessage.value = "";

    if (!phone.value || !password.value) {
      alert("로그인 정보를 모두 입력해주세요!");
      return;
    }

    if (phone.value.length !== 11) {
      alert("휴대폰 번호를 정확히 입력해주세요!");
      return;
    }

    // --- encrypt가 동기/비동기 모두 대응
    const encryptedPassword = await Promise.resolve(encrypt(password.value));

    const snap = await getDocs(
      query(
        collection(db, "users"),
        where("userPhone", "==", phone.value),
        where("userPassword", "==", encryptedPassword)
      )
    );

    if (snap.empty) {
      errorMessage.value = "회원 정보가 없습니다. 회원가입을 진행해주세요!";
      return;
    }

    const user = snap.docs[0].data();
    const uid = user.userId;

    // --- loginProcess가 동기/비동기 모두 대응
    const isLogged = await Promise.resolve(loginProcess(uid));

    if (!isLogged) {
      alert("로그인 처리에 실패했습니다. 다시 시도해주세요.");
      return;
    }

    // --- 역할별 이동
    switch (role.value) {
      case "consumer":
        window.location.href = "/";
        break;
      case "admin":
        window.location.href = "/admin";
        break;
      default:
        window.location.href = "/";
        break;
    }
  }).catch((error) => {
    console.error("로그인 실패:", error);
    alert("로그인 중 오류가 발생했습니다.");
  });
</script>

<style scoped lang="scss">
.login-container {
  max-width: 480px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 24px;
  border-radius: 8px;
  background-color: #fff;
  text-align: center;
  height: 100%;

  > img {
    width: 128px;
  }

  > div {
    margin-top: 48px;
    width: 100%;

    > .role-container {
      display: flex;
      align-items: center;
      gap: 16px;
      justify-content: end;
      margin-bottom: 16px;

      > div {
        display: flex;
        align-items: center;
        gap: 6px;

        > input {
          accent-color: #007bff;
          width: 16px;
          height: 16px;
        }

        > label {
          font-size: 14px;
        }
      }
    }

    .form > input {
      padding: 12px 8px;
      width: 100%;
      border: none;
      border-bottom: 1.5px solid #ccc;
      margin-bottom: 8px;
      font-size: 16px;

      &:focus {
        outline: none;
        border-bottom-color: #007bff;
      }
    }

    > button {
      margin-top: 16px;
      padding: 12px 16px;
      font-size: 16px;
      background-color: #007bff;
      color: #fff;
      border: none;
      border-radius: 4px;
      cursor: pointer;
      width: 100%;
      font-weight: 700;

      &:hover {
        background-color: #0069d9;
      }
    }

    > .tool-container {
      margin-top: 16px;
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 16px;

      > a,
      button {
        border: none;
        background: none;
        font-size: 14px;
        color: #666;
        cursor: pointer;
      }

      > hr {
        height: 12px;
      }
    }

    > p {
      font-size: 14px;
      margin-top: 8px;
      font-weight: 700;
      color: #007bff;
    }
  }

  > dialog {
    position: fixed;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    background-color: #fff;
    border-radius: 8px;
    width: 90%;
    max-width: 480px;
    box-shadow: 8px 8px 16px rgba(0, 0, 0, 0.25);
    border: none;

    &::backdrop {
      background: rgba(0, 0, 0, 0.5);
    }

    > header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 8px 16px;
      position: sticky;
      top: 0;
      border-bottom: 1px solid rgba(0, 0, 0, 0.25);
      background: #efefef;

      > h2 {
        font-size: 18px;
      }

      > button {
        border: none;
        border-radius: 4px;
        padding: 4px 8px;
        background-color: #007bff;
        color: #fff;
        font-weight: 700;
        cursor: pointer;

        > span {
          font-size: 16px;
        }
      }
    }

    > main {
      padding: 8px 16px;
      > img {
        width: 100%;
      }

      > input {
        padding: 12px 8px;
        width: 100%;
        border: none;
        border-bottom: 1.5px solid #ccc;
        margin-bottom: 8px;
        font-size: 16px;

        &:focus {
          outline: none;
          border-bottom-color: #007bff;
        }
      }

      > button {
        margin-top: 16px;
        padding: 12px 16px;
        font-size: 16px;
        background-color: #007bff;
        color: #fff;
        border: none;
        border-radius: 4px;
        cursor: pointer;
        width: 100%;
        font-weight: 700;

        &:hover {
          background-color: #0069d9;
        }
      }
    }
  }
}
.error {
  margin-top: 16px;
  font-size: 14px;
  font-weight: 700;
  color: red;
}
</style>
